import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Row, Col, Card, Table, Button, Input, Modal, Typography, Tag, Space, Select, Drawer, Descriptions, Divider, Spin } from "antd";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, PlusOutlined, TeamOutlined, UserAddOutlined, EyeOutlined, FilterOutlined } from "@ant-design/icons";
import axios from "axios";
import { baseUrl } from "../../config";

const { confirm } = Modal;
const { Search } = Input;
const { Title, Text } = Typography;
const { Option } = Select;

const KULRISHI_OPTIONS = [
  'Uddalak Ji', 'Vasishtha Ji', 'Kashyapa Ji', 'Parasara Ji',
  'Piplad Ji', 'Shandilya Ji', 'Bharadwaj Ji', 'Gautam Rishi'
];

function FamiliesList() {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [dataList, setDataList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(parseInt(queryParams.get('page')) || 1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Filter states
  const [filters, setFilters] = useState({ country_id: '', state_id: '', district_id: '', tehsil_id: '', panchayat_id: '', village_id: '', gender: '', kulrishi: '', pin_code: '' });

  // Dropdown data for filters
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [tehsils, setTehsils] = useState([]);
  const [panchayats, setPanchayats] = useState([]);
  const [villages, setVillages] = useState([]);

  // View drawer
  const [viewVisible, setViewVisible] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewMembers, setViewMembers] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const authHeader = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };

  useEffect(() => {
    axios.get(`${baseUrl}/pop-country/list-all`, { headers: authHeader })
      .then(r => { if (r.data.status) setCountries(r.data.result); });
  }, []);

  const loadStates = (country_id) => {
    setStates([]); setDistricts([]); setTehsils([]); setPanchayats([]); setVillages([]);
    if (!country_id) return;
    axios.get(`${baseUrl}/pop-states/list-all`, { headers: authHeader, params: { country_id } })
      .then(r => { if (r.data.status) setStates(r.data.result); });
  };
  const loadDistricts = (state_id) => {
    setDistricts([]); setTehsils([]); setPanchayats([]); setVillages([]);
    if (!state_id) return;
    axios.get(`${baseUrl}/pop-districts/list-all`, { headers: authHeader, params: { state_id } })
      .then(r => { if (r.data.status) setDistricts(r.data.result); });
  };
  const loadTehsils = (district_id) => {
    setTehsils([]); setPanchayats([]); setVillages([]);
    if (!district_id) return;
    axios.get(`${baseUrl}/pop-tehsils/list-all`, { headers: authHeader, params: { district_id } })
      .then(r => { if (r.data.status) setTehsils(r.data.result); });
  };
  const loadPanchayats = (tehsil_id) => {
    setPanchayats([]); setVillages([]);
    if (!tehsil_id) return;
    axios.get(`${baseUrl}/pop-panchayat/list-all`, { headers: authHeader, params: { tehsil_id } })
      .then(r => { if (r.data.status) setPanchayats(r.data.result); });
  };
  const loadVillages = (panchayat_id) => {
    setVillages([]);
    if (!panchayat_id) return;
    axios.get(`${baseUrl}/pop-village/list-all`, { headers: authHeader, params: { panchayat_id } })
      .then(r => { if (r.data.status) setVillages(r.data.result); });
  };

  const fetchList = async (page = currentPage, searchVal = search, f = filters) => {
    setLoading(true);
    try {
      const params = { page, limit, search: searchVal };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${baseUrl}/pop-families`, { headers: authHeader, params });
      if (res.data.status) {
        setDataList(res.data.result.map((item, i) => ({ ...item, key: (page - 1) * limit + i + 1 })));
        setTotalCount(res.data.totalCount);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, [currentPage]);

  const applyFilters = () => { setCurrentPage(1); fetchList(1, search, filters); };
  const resetFilters = () => {
    const empty = { country_id: '', state_id: '', district_id: '', tehsil_id: '', panchayat_id: '', village_id: '', gender: '', kulrishi: '', pin_code: '' };
    setFilters(empty);
    setStates([]); setDistricts([]); setTehsils([]); setPanchayats([]); setVillages([]);
    setCurrentPage(1);
    fetchList(1, search, empty);
  };

  const handleDelete = (id) => {
    confirm({
      title: 'Are you sure you want to delete this record?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes, Delete', okType: 'danger', cancelText: 'Cancel',
      onOk: async () => {
        try {
          await axios.delete(`${baseUrl}/pop-families/${id}`, { headers: authHeader });
          fetchList();
        } catch (err) { console.error(err); }
      }
    });
  };

  const handleView = async (record) => {
    setViewData(record);
    setViewMembers([]);
    setActiveTab('general');
    setViewVisible(true);
    setViewLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/pop-family-member`, {
        headers: authHeader,
        params: { limit: 'all', family_id: record.id }
      });
      if (res.data.status) setViewMembers(res.data.result);
    } catch (err) { console.error(err); }
    finally { setViewLoading(false); }
  };

  const columns = [
    { title: '#', dataIndex: 'key', key: 'key', width: 55 },
    { title: 'Head of Family', dataIndex: 'head_family', key: 'head_family', width: 150 },
    { title: 'Father Name', dataIndex: 'head_father_name', key: 'head_father_name', width: 140 },
    { title: 'Phone No', dataIndex: 'phone_no_head', key: 'phone_no_head', width: 120 },
    { title: 'Sub Caste', dataIndex: 'sub_caste', key: 'sub_caste', width: 110 },
    { title: 'Kulrishi', dataIndex: 'kulrishi', key: 'kulrishi', width: 120 },
    { title: 'Kuldevi', dataIndex: 'kildevi', key: 'kildevi', width: 110 },
    { title: 'Gender', dataIndex: 'gender', key: 'gender', width: 80 },
    { title: 'Village', dataIndex: 'village_name', key: 'village_name', width: 120 },
    { title: 'Panchayat', dataIndex: 'panchayat_name', key: 'panchayat_name', width: 120 },
    { title: 'Pin Code', dataIndex: 'pin_code', key: 'pin_code', width: 90 },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 90,
      render: s => <Tag color={String(s) === '1' ? 'green' : 'red'}>{String(s) === '1' ? 'Active' : 'Inactive'}</Tag>
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 260,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(record)} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>View</Button>
          <Button size="small" icon={<TeamOutlined />} onClick={() => history.push(`/pop-family-member?family_id=${record.id}`)} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Members</Button>
          <Button size="small" icon={<UserAddOutlined />} onClick={() => history.push(`/pop-family-member/add?family_id=${record.id}`)} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Add Member</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => history.push(`/pop-families/update/${record.id}`)} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit</Button>
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>Delete</Button>
        </Space>
      )
    }
  ];

  const memberColumns = [
    { title: '#', dataIndex: 'index', key: 'index', width: 50, render: (_, __, i) => i + 1 },
    { title: 'Name', dataIndex: 'member_name', key: 'member_name' },
    { title: 'Relation', dataIndex: 'relation_type', key: 'relation_type' },
    { title: 'Relation With', dataIndex: 'relation_name_with', key: 'relation_name_with' },
    { title: 'Education', dataIndex: 'education_name', key: 'education_name' },
    { title: 'Occupation', dataIndex: 'occupation_name', key: 'occupation_name' },
    { title: 'Gender', dataIndex: 'gender_type', key: 'gender_type' },
    { title: 'Phone', dataIndex: 'phone_no', key: 'phone_no' },
    { title: 'Email', dataIndex: 'email_address', key: 'email_address' },
    { title: 'Married', dataIndex: 'married_status', key: 'married_status' },
    { title: 'DOB', dataIndex: 'dob', key: 'dob' },
    { title: 'Age', dataIndex: 'age', key: 'age', width: 60 },
    { title: 'Address', dataIndex: 'address', key: 'address' },
  ];

  return (
    <div className="tabled">
      <Row gutter={[24, 0]}>
        <Col span={24}>
          <Card
            title={<span style={{ color: '#1e293b', fontWeight: 700, fontSize: 15 }}>Families List</span>}
            headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0' }}
            extra={
              <Space>
                <Button icon={<FilterOutlined />} onClick={() => setShowFilters(v => !v)} style={{ background: '#fff', borderColor: '#fff', color: '#7a0000', fontWeight: 600 }}>
                  {showFilters ? 'Hide Filters' : 'Filters'}
                </Button>
                <Button icon={<PlusOutlined />} onClick={() => history.push('/pop-families/add')} style={{ background: '#fff', borderColor: '#fff', color: '#7a0000', fontWeight: 600 }}>
                  Add New
                </Button>
              </Space>
            }
          >
            {/* Filters */}
            {showFilters && (
              <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '16px 12px', marginBottom: 16 }}>
                <Row gutter={[12, 12]}>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="Country" style={{ width: '100%' }} allowClear value={filters.country_id || undefined}
                      onChange={v => { setFilters(f => ({ ...f, country_id: v || '', state_id: '', district_id: '', tehsil_id: '', panchayat_id: '', village_id: '' })); loadStates(v); }}>
                      {countries.map(c => <Option key={c.id} value={String(c.id)}>{c.name}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="State" style={{ width: '100%' }} allowClear value={filters.state_id || undefined}
                      onChange={v => { setFilters(f => ({ ...f, state_id: v || '', district_id: '', tehsil_id: '', panchayat_id: '', village_id: '' })); loadDistricts(v); }}>
                      {states.map(s => <Option key={s.id} value={String(s.id)}>{s.name}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="District" style={{ width: '100%' }} allowClear value={filters.district_id || undefined}
                      onChange={v => { setFilters(f => ({ ...f, district_id: v || '', tehsil_id: '', panchayat_id: '', village_id: '' })); loadTehsils(v); }}>
                      {districts.map(d => <Option key={d.id} value={String(d.id)}>{d.name}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="Tehsil" style={{ width: '100%' }} allowClear value={filters.tehsil_id || undefined}
                      onChange={v => { setFilters(f => ({ ...f, tehsil_id: v || '', panchayat_id: '', village_id: '' })); loadPanchayats(v); }}>
                      {tehsils.map(t => <Option key={t.id} value={String(t.id)}>{t.name}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="Panchayat" style={{ width: '100%' }} allowClear value={filters.panchayat_id || undefined}
                      onChange={v => { setFilters(f => ({ ...f, panchayat_id: v || '', village_id: '' })); loadVillages(v); }}>
                      {panchayats.map(p => <Option key={p.id} value={String(p.id)}>{p.name}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="Village" style={{ width: '100%' }} allowClear value={filters.village_id || undefined}
                      onChange={v => setFilters(f => ({ ...f, village_id: v || '' }))}>
                      {villages.map(v => <Option key={v.id} value={String(v.id)}>{v.name}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="Gender" style={{ width: '100%' }} allowClear value={filters.gender || undefined}
                      onChange={v => setFilters(f => ({ ...f, gender: v || '' }))}>
                      <Option value="male">Male</Option>
                      <Option value="female">Female</Option>
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Select placeholder="Kulrishi" style={{ width: '100%' }} allowClear value={filters.kulrishi || undefined}
                      onChange={v => setFilters(f => ({ ...f, kulrishi: v || '' }))}>
                      {KULRISHI_OPTIONS.map(k => <Option key={k} value={k}>{k}</Option>)}
                    </Select>
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Input placeholder="Pin Code" value={filters.pin_code}
                      onChange={e => setFilters(f => ({ ...f, pin_code: e.target.value }))} />
                  </Col>
                  <Col xs={12} sm={8} md={4}>
                    <Space>
                      <Button onClick={applyFilters} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Apply</Button>
                      <Button onClick={resetFilters}>Reset</Button>
                    </Space>
                  </Col>
                </Row>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="Search by name, phone..."
                onSearch={val => { setSearch(val); setCurrentPage(1); fetchList(1, val, filters); }}
                allowClear
                style={{ width: 300 }}
                enterButton={<Button style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Search</Button>}
              />
            </div>

            <Table
              columns={columns}
              dataSource={dataList}
              loading={loading}
              pagination={{ current: currentPage, pageSize: limit, total: totalCount, onChange: p => setCurrentPage(p), showTotal: t => `Total ${t} families` }}
              scroll={{ x: 'max-content' }}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      {/* View Drawer */}
      <Drawer
        title={viewData ? `Family: ${viewData.head_family}` : 'Family Details'}
        width={900}
        open={viewVisible}
        onClose={() => setViewVisible(false)}
        bodyStyle={{ padding: 0 }}
      >
        {viewData && (
          <div>
            {/* Tab buttons */}
            <div style={{ display: 'flex', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
              <Button
                type="text"
                style={{ borderRadius: 0, borderBottom: activeTab === 'general' ? '2px solid #7a0000' : '2px solid transparent', color: activeTab === 'general' ? '#7a0000' : '#555', fontWeight: activeTab === 'general' ? 600 : 400, padding: '12px 24px', height: 'auto' }}
                onClick={() => setActiveTab('general')}
              >
                General Details
              </Button>
              <Button
                type="text"
                style={{ borderRadius: 0, borderBottom: activeTab === 'members' ? '2px solid #7a0000' : '2px solid transparent', color: activeTab === 'members' ? '#7a0000' : '#555', fontWeight: activeTab === 'members' ? 600 : 400, padding: '12px 24px', height: 'auto' }}
                onClick={() => setActiveTab('members')}
              >
                Family Members ({viewMembers.length})
              </Button>
            </div>

            {/* General Details tab */}
            {activeTab === 'general' && (
              <div style={{ padding: 24 }}>
                <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                  <Descriptions.Item label="Head of Family"><strong>{viewData.head_family}</strong></Descriptions.Item>
                  <Descriptions.Item label="Father Name">{viewData.head_father_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Phone No">{viewData.phone_no_head || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Sub Caste (Gotra)">{viewData.sub_caste || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Kulrishi">{viewData.kulrishi || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Kuldevi">{viewData.kildevi || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Gender">{viewData.gender ? viewData.gender.charAt(0).toUpperCase() + viewData.gender.slice(1) : '-'}</Descriptions.Item>
                  <Descriptions.Item label="Country">{viewData.country_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="State">{viewData.state_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="District">{viewData.district_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="City/Tehsil">{viewData.tehsil_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Gram Panchayat">{viewData.panchayat_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Village">{viewData.village_name || '-'}</Descriptions.Item>
                  <Descriptions.Item label="House in Village">{viewData.house_village || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Panchayat Committee">{viewData.panchayat_committee || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Pin Code">{viewData.pin_code || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Status">
                    <Tag color={String(viewData.status) === '1' ? 'green' : 'red'}>{String(viewData.status) === '1' ? 'Active' : 'Inactive'}</Tag>
                  </Descriptions.Item>
                </Descriptions>
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                  <Button icon={<EditOutlined />} onClick={() => { setViewVisible(false); history.push(`/pop-families/update/${viewData.id}`); }} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Edit Family</Button>
                  <Button icon={<UserAddOutlined />} onClick={() => { setViewVisible(false); history.push(`/pop-family-member/add?family_id=${viewData.id}`); }} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>Add Member</Button>
                </div>
              </div>
            )}

            {/* Family Members tab */}
            {activeTab === 'members' && (
              <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button icon={<UserAddOutlined />} onClick={() => { setViewVisible(false); history.push(`/pop-family-member/add?family_id=${viewData.id}`); }} style={{ background: '#7a0000', borderColor: '#7a0000', color: '#fff' }}>
                    Add Member
                  </Button>
                </div>
                {viewLoading ? (
                  <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                ) : viewMembers.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>No Members Added Yet</div>
                ) : (
                  <Table
                    dataSource={viewMembers.map((m, i) => ({ ...m, key: i }))}
                    columns={memberColumns}
                    pagination={false}
                    scroll={{ x: 1200 }}
                    size="small"
                    bordered
                  />
                )}
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default FamiliesList;
